import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Send as SendIcon,
} from '@mui/icons-material';
import { useChat } from '../contexts/ChatContext';

const ChatInput: React.FC = () => {
  const { sendMessage, isLoading } = useChat();
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  useEffect(() => {
    // Focus input on component mount
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    // Listen for prompt insertion events from ChatPage
    const handleInsertPrompt = (e: CustomEvent) => {
      setMessage(e.detail);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    };

    document.addEventListener('insertPrompt', handleInsertPrompt as EventListener);
    return () => {
      document.removeEventListener('insertPrompt', handleInsertPrompt as EventListener);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || isLoading) {
      return;
    }

    const messageToSend = message.trim();
    setMessage('');
    
    try {
      await sendMessage(messageToSend);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore message on error
      setMessage(messageToSend);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Box
      sx={{
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
      }}
    >
      {/* Input Container */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          position: 'relative'
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          placeholder="Message CoreSupport..."
          disabled={isLoading}
          variant="outlined"
          InputProps={{
            // Attach file icon removed as requested
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '24px',
              backgroundColor: theme.palette.mode === 'dark' 
                ? 'rgba(30, 30, 35, 0.6) !important' 
                : '#ffffff !important',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              padding: '12px 14px',
              paddingRight: '52px', // Space for send button
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.2)'
                : '0 4px 20px rgba(0,0,0,0.03)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 24px rgba(0,0,0,0.3)'
                  : '0 4px 24px rgba(99, 102, 241, 0.08)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-focused': {
                borderColor: theme.palette.primary.main,
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 8px 32px rgba(99, 102, 241, 0.15)'
                  : '0 8px 32px rgba(99, 102, 241, 0.12)',
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(30, 30, 35, 0.8) !important' 
                  : '#ffffff !important',
                transform: 'translateY(-2px)',
              },
              '& fieldset': {
                border: 'none',
              },
              '& input': {
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
              '& textarea': {
                color: theme.palette.text.primary,
                fontWeight: 500,
                lineHeight: 1.5,
              }
            }
          }}
        />
        
        {/* Send Button */}
        <Box sx={{ 
          position: 'absolute',
          right: 8,
          bottom: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          zIndex: 1
        }}>
          {/* Voice input icon removed as requested */}
          
          <IconButton
            type="submit"
            disabled={!message.trim() || isLoading}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '12px',
              backgroundColor: message.trim() 
                ? theme.palette.primary.main 
                : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              color: message.trim() ? 'white' : theme.palette.text.disabled,
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              '&:hover': {
                backgroundColor: message.trim() 
                  ? theme.palette.primary.dark 
                  : theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                transform: message.trim() ? 'scale(1.1) rotate(-10deg)' : 'none',
                boxShadow: message.trim() ? '0 4px 12px rgba(99, 102, 241, 0.4)' : 'none'
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
              '&:disabled': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                color: theme.palette.text.disabled
              },
            }}
          >
            <SendIcon sx={{ fontSize: 18, ml: 0.5 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInput;
